import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { verifyEmailAndProcessInvitation, verifyEmailForEmployeeSelfRegistration } from '../../common/fetch';
import { confirmMsg, t } from '../../common/i18n';
import { FormGridFieldset } from '../../common/react-hook-form/FormGridFieldset/FormGridFieldset';
import { Email, PhoneNumber } from '../../common/react-hook-form/fields';
import { useAuth } from '../../public/site/AuthProvider';
import { VerifyOtp } from '../EoeSelfRegistration/VerifyOtp';
import type { OnlineApiError } from '../../generated/api/onlineApiError';

import './EoeInvitationAcceptance.scss';

export enum EoeInvitationAcceptanceStep {
  ENTER_EMAIL_AND_PHONE = 1,
  VERIFY_EMAIL_OTP = 2,
}

const InvitationAcceptanceSessionKey = 'employee-invitation-acceptance-state';

export interface EoeInvitationAcceptanceState {
  step: EoeInvitationAcceptanceStep;
  email?: string;
  phoneNumber?: string;
  validUntil?: number;
}

export const getEmployeeInvitationAcceptanceStateFromSession = (): EoeInvitationAcceptanceState | undefined => {
  const stateString = sessionStorage.getItem(InvitationAcceptanceSessionKey);
  if (stateString) {
    return JSON.parse(stateString) as EoeInvitationAcceptanceState;
  }
  return undefined;
};

const setEmployeeInvitationAcceptanceStateToSession = (state: EoeInvitationAcceptanceState) => {
  sessionStorage.setItem(InvitationAcceptanceSessionKey, JSON.stringify(state));
};

const clearEmployeeInvitationAcceptanceStateFromSession = () => {
  sessionStorage.removeItem(InvitationAcceptanceSessionKey);
};

interface EnterEmailAndPhoneProps {
  requestVerifyEmail: (email: string, phoneNumber?: string) => void;
  sending: boolean;
  invitedEmail?: string;
}

const EnterEmailAndPhone = ({ requestVerifyEmail, sending, invitedEmail }: EnterEmailAndPhoneProps) => {
  const methods = useForm({
    mode: 'all',
    defaultValues: {
      email: invitedEmail ?? '',
      phoneNumber: '',
    },
  });

  const onSubmit = async (values: { email: string; phoneNumber?: string }) => {
    requestVerifyEmail(values.email, values.phoneNumber);
  };

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <FormGridFieldset>
            <h3 className={dsClass.MARGIN_TOP_0}>{t.RXQO('Confirm your email address.')}</h3>
            <p className={dsClass.MARGIN_TOP_0}>
              {t.XYOP('Start Employee OmaElisa activation by checking and confirming your email address.')}
            </p>
            <Email
              label={t.R9VH('Email address (work)')}
              readonly={false}
              errorText={t.NJLM('Check the email address. Enter the address in the format name@example.com')}
              tooltip={t.NUFF(
                'Use only the email that you have access to. If you are a new employee and do not yet have access to your work email, you can update the work email later.'
              )}
            />
            <PhoneNumber label={t.DIRC('Phone number (work)')} readonly={false} required={false} />
            <CL.Button type="submit" size="m" loading={sending}>
              {t.QVYK(confirmMsg)}
            </CL.Button>
          </FormGridFieldset>
        </form>
      </FormProvider>
    </div>
  );
};

const ErrorDisclaimer = () => {
  return (
    <CL.Disclaimer
      text={t.GEPH(
        'Unfortunately, an error occurred in our system. Please try again shortly. We apologize for the inconvenience.'
      )}
      disclaimerType="alert"
      icon={<CL.Icon icon="warning" color="error" size="l" />}
      className={`${dsClass.MARGIN_TOP_4} `}
    />
  );
};

export interface EmployeeInvitationAcceptanceProps {
  employeeInvitationAcceptanceState?: EoeInvitationAcceptanceState;
  invitedEmail?: string;
}

export const EoeInvitationAcceptance = ({
  employeeInvitationAcceptanceState,
  invitedEmail,
}: EmployeeInvitationAcceptanceProps) => {
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [expiredOtp, setExpiredOtp] = useState(false);
  const [currentState, setCurrentState] = useState<EoeInvitationAcceptanceState>(
    employeeInvitationAcceptanceState ?? {
      step: EoeInvitationAcceptanceStep.ENTER_EMAIL_AND_PHONE,
    }
  );
  const { validateAuth } = useAuth();
  const navigate = useNavigate();

  const setNextStateAndSession = (state: EoeInvitationAcceptanceState, errored?: boolean) => {
    setCurrentState(state);
    setEmployeeInvitationAcceptanceStateToSession(state);
    setIsError(!!errored);
  };

  const requestVerifyEmail = async (email: string, phoneNumber?: string) => {
    setLoading(true);
    try {
      await verifyEmailForEmployeeSelfRegistration({ email }, false);
      const verifyEmailState: EoeInvitationAcceptanceState = {
        step: EoeInvitationAcceptanceStep.VERIFY_EMAIL_OTP,
        email,
        phoneNumber: phoneNumber ?? undefined,
        validUntil: Date.now() + 10 * 60 * 1000,
      };
      setNextStateAndSession(verifyEmailState);
    } catch (error) {
      if (error instanceof Response && error.status === 409) {
        const onlineApiError: OnlineApiError | null = await error.json().catch(() => null);
        if (onlineApiError?.error?.toLowerCase().includes('email already verified')) {
          // Email already verified — go directly to process invitation
          try {
            await verifyEmailAndProcessInvitation({ email, otp: '', mobileNumber: phoneNumber });
            validateAuth();
            clearEmployeeInvitationAcceptanceStateFromSession();
            navigate(paths.EMPLOYEE_HOME);
            return;
          } catch {
            setIsError(true);
            return;
          }
        }
      }
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const resendVerifyEmail = async (): Promise<boolean> => {
    try {
      const email = currentState.email!;
      await verifyEmailForEmployeeSelfRegistration({ email }, true);
      const verifyEmailState: EoeInvitationAcceptanceState = {
        step: EoeInvitationAcceptanceStep.VERIFY_EMAIL_OTP,
        email,
        phoneNumber: currentState.phoneNumber,
        validUntil: Date.now() + 10 * 60 * 1000,
      };
      setNextStateAndSession(verifyEmailState);
      return true;
    } catch {
      setIsError(true);
      return false;
    } finally {
      setExpiredOtp(false);
      setLoading(false);
    }
  };

  const verifyEmailOtp = async (otp: string) => {
    setLoading(true);
    if (currentState.validUntil && Date.now() > currentState.validUntil) {
      setExpiredOtp(true);
      setLoading(false);
      return;
    }
    try {
      await verifyEmailAndProcessInvitation({
        email: currentState.email!,
        otp,
        mobileNumber: currentState.phoneNumber,
      });
      validateAuth();
      clearEmployeeInvitationAcceptanceStateFromSession();
      navigate(paths.EMPLOYEE_HOME);
    } catch (error) {
      if (error instanceof Response && error.status === 409) {
        const onlineApiError: OnlineApiError | null = await error.json().catch(() => null);
        if (onlineApiError?.error?.toLowerCase().includes('otp is expired')) {
          setExpiredOtp(true);
          return;
        }
      }
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const goBackToInput = () => {
    const initiateState: EoeInvitationAcceptanceState = {
      step: EoeInvitationAcceptanceStep.ENTER_EMAIL_AND_PHONE,
    };
    setNextStateAndSession(initiateState);
  };

  const contentByStep = () => {
    switch (currentState.step) {
      case EoeInvitationAcceptanceStep.ENTER_EMAIL_AND_PHONE:
        return (
          <EnterEmailAndPhone requestVerifyEmail={requestVerifyEmail} sending={loading} invitedEmail={invitedEmail} />
        );
      case EoeInvitationAcceptanceStep.VERIFY_EMAIL_OTP:
        return (
          <VerifyOtp
            email={currentState.email}
            validUntil={currentState.validUntil!}
            verifying={loading}
            expiredOtp={expiredOtp}
            verifyOtp={verifyEmailOtp}
            resendOtp={resendVerifyEmail}
            goBack={goBackToInput}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="of-employee-invitation-acceptance__root">
      <div className="of-employee-invitation-acceptance__body">
        {contentByStep()}
        {isError && <ErrorDisclaimer />}
      </div>
    </div>
  );
};
